import { useEffect, useState } from 'react';
import { Tooltip } from 'bootstrap';

import { useData } from '../../utils/hooks/useData';
import { formatFromAndToQuery, calculatePercentageDifference, handleExport } from '../lib';
import { AnalyticsStoreProvider, useAnalyticsStoreContext } from '../contexts';

import dayjs from 'dayjs';

import StatCard from '../../card/stat-card/StatCard';
import AnalyticsError from '../AnalyticsError';
import DateRangeComparatorInput from '../inputs/DateRangeComparatorInput';
import TableView from './_components/TableView';
import StandardLayout from '../../layout/StandardLayout';

function Searches({ category_and_values, endpoints }) {
  const {
    job_searches_all_time_count_api_v1_cms_analytics_path,
    job_searches_period_count_api_v1_cms_analytics_path,
    job_search_used_filters_all_time_count_api_v1_cms_analytics_path,
    job_search_used_filters_period_count_api_v1_cms_analytics_path,
    api_v1_cms_data_exports_path,
  } = endpoints;

  const [isExporting, setIsExporting] = useState(false);

  const dateRange = useAnalyticsStoreContext((state) => state.data.first.dateRange);
  const dateRangeTwo = useAnalyticsStoreContext((state) => state.data.second.dateRange);
  const isComparing = useAnalyticsStoreContext((state) => state.isComparing);

  const { data: allTimeJobSearches } = useData(`${job_searches_all_time_count_api_v1_cms_analytics_path}.json`);

  const { data: periodJobSearches } = useData(`${job_searches_period_count_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRange)}`);

  const { data: periodJobSearchesTwo } = useData(
    `${job_searches_period_count_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
  );

  const {
    data: allTimeCount,
    isLoading: isAllTimeCountLoading,
    error: allTimeCountError,
  } = useData(`${job_search_used_filters_all_time_count_api_v1_cms_analytics_path}.json`);

  const {
    data: periodCount,
    isLoading: isPeriodCountLoading,
    error: periodCountError,
  } = useData(`${job_search_used_filters_period_count_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRange)}`);

  const {
    data: periodCountTwo,
    isLoading: isPeriodCountLoadingTwo,
    error: periodCountErrorTwo,
  } = useData(
    `${job_search_used_filters_period_count_api_v1_cms_analytics_path}.json?${formatFromAndToQuery(dateRangeTwo)}`,
    isComparing && dateRangeTwo.from && dateRangeTwo.to,
    {},
  );

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].forEach((tooltipTriggerEl) => new Tooltip(tooltipTriggerEl));
  }, []);

  const handleExportOnClick = () =>
    handleExport(isExporting, setIsExporting, api_v1_cms_data_exports_path, {
      data_export: {
        type: 'job_searches',
        date_range_from: dayjs(dateRange.from).format('YYYY-MM-DD'),
        date_range_to: dayjs(dateRange.to).format('YYYY-MM-DD'),
        date_range_two_from: dayjs(dateRangeTwo.from).format('YYYY-MM-DD'),
        date_range_two_to: dayjs(dateRangeTwo.to).format('YYYY-MM-DD'),
      },
    });

  return (
    <StandardLayout
      h6="Insights"
      pageH1="Job Searches"
      button={
        <div className="d-flex gap-3 align-items-center">
          <button
            className="btn btn-primary"
            onClick={handleExportOnClick}
            disabled={isAllTimeCountLoading || isPeriodCountLoading || isPeriodCountLoadingTwo || isExporting}
          >
            Export CSV
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-4" style={{ maxWidth: 1100 }}>
        <div className="d-flex justify-content-end">
          <DateRangeComparatorInput />
        </div>
        <div className="d-flex gap-4">
          <StatCard title="Total Searches (All Time)" value={allTimeJobSearches ?? '-'} iconClass="fe fe-search" />
          <StatCard
            title="Total Searches (Selected Period)"
            value={periodJobSearches ?? '-'}
            comparingValue={isComparing ? `${periodJobSearchesTwo ?? '-'} (${calculatePercentageDifference(periodJobSearches, periodJobSearchesTwo)})` : null}
            iconClass="fe fe-search"
          />
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2">
            <h2 className="mb-0 ">Filters Statistics</h2>
            <i
              type="button"
              className="fe fe-info mb-0"
              data-bs-toggle="tooltip"
              data-bs-html="true"
              data-bs-title="<b>Category Values</b> used for job filters will appear here."
            />
          </div>
          {isAllTimeCountLoading || isPeriodCountLoading || isPeriodCountLoadingTwo ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: 300 }}>
              <div className="spinner-border" role="loading">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : allTimeCountError || periodCountError || periodCountErrorTwo ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: 100 }}>
              {allTimeCountError && (
                <div className="d-flex gap-2">
                  <span>All time count:</span>
                  <AnalyticsError errorMsg={allTimeCountError} />
                </div>
              )}
              {periodCountError && (
                <div className="d-flex gap-2">
                  <span>First period count:</span>
                  <AnalyticsError errorMsg={periodCountError} />
                </div>
              )}
              {periodCountErrorTwo && (
                <div className="d-flex gap-2">
                  <span>Second period count:</span>
                  <AnalyticsError errorMsg={periodCountErrorTwo} />
                </div>
              )}
            </div>
          ) : (
            <div className="pt-2">
              <TableView {...{ category_and_values, allTimeCount, periodCount, periodCountTwo, isComparing }} />
            </div>
          )}
        </div>
      </div>
    </StandardLayout>
  );
}

export default function JobSearches(props) {
  return (
    <AnalyticsStoreProvider>
      <Searches {...props} />
    </AnalyticsStoreProvider>
  );
}
